import React, { useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { createCollection } from '../services/taskService';
import BoardModal, { BMCancelButton, BMPrimaryButton, BM_LABEL, BM_TITLE_INPUT } from '../components/BoardModal';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  nextSortOrder: number;
  onCreated: () => void;
  onCancel: () => void;
}

const SWATCHES: { hex: string; label: string }[] = [
  { hex: '#0E7490', label: 'Nordic Steel' },
  { hex: '#17A2C0', label: 'Steel Bright' },
  { hex: '#6D28D9', label: 'Violet' },
  { hex: '#7C3AED', label: 'Deep Violet' },
  { hex: '#0D6E6E', label: 'Teal Green' },
  { hex: '#059669', label: 'Emerald' },
  { hex: '#D97706', label: 'Amber' },
  { hex: '#DC2626', label: 'Red' },
  { hex: '#374151', label: 'Slate' },
  { hex: '#475569', label: 'Blue Slate' },
];

export default function CreateCollectionModal({ pca, projectId, nextSortOrder, onCreated, onCancel }: Props) {
  const [name, setName]   = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true); setError(null);
    try {
      await createCollection(pca, projectId, trimmed, nextSortOrder, color);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BoardModal open title="New Collection" onClose={onCancel}
      footer={<>
        <BMCancelButton onClick={onCancel} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 16 }}>
        <label style={BM_LABEL}>Collection name</label>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
          placeholder="e.g. In Review"
          style={BM_TITLE_INPUT}
          disabled={saving}
        />
      </div>
      <div>
        <label style={BM_LABEL}>Header color</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button"
            onClick={() => setColor(null)}
            title="Default (brand)"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: 'linear-gradient(135deg, #0E7490, #0A5568)',
              border: color === null ? '2px solid #0E7490' : '1px solid #DDE0E6',
              cursor: 'pointer',
            }}
          />
          {SWATCHES.map(s => {
            const active = color === s.hex;
            return (
              <button key={s.hex} type="button"
                onClick={() => setColor(s.hex)}
                title={s.label}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: s.hex, border: 'none',
                  cursor: 'pointer',
                  outline: active ? '2px solid #fff' : 'none',
                  outlineOffset: active ? 2 : 0,
                  boxShadow: active ? '0 0 0 3px #0E7490' : 'none',
                }}
              />
            );
          })}
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 12 }}>{error}</div>}
    </BoardModal>
  );
}
